import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, FolderLock, ArrowUpRight, X } from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

const PIPELINE_STAGES = [
    { v: "prospect", l: "Prospect" },
    { v: "qualification", l: "Qualification" },
    { v: "audit", l: "Audit" },
    { v: "structuration", l: "Structuration" },
    { v: "discussions", l: "Discussions" },
    { v: "finalisation", l: "Finalisation" },
    { v: "clos", l: "Clos" },
];

const SOURCE_OPTIONS_LIST = [
    "Réseau", "Recommandation", "LinkedIn", "Instagram",
    "Family Office", "Collectivité", "Partenaire", "Événement", "Autre",
].map((v) => ({ v, l: v }));

const MATURITY_LEVELS_LIST = [
    { v: "faible", l: "Faible" },
    { v: "sérieux", l: "Sérieux" },
    { v: "avancé", l: "Avancé" },
    { v: "prioritaire", l: "Prioritaire" },
];

const STATUS = [
    { v: "draft", l: "Brouillon" },
    { v: "audit", l: "Audit initial" },
    { v: "analyse", l: "Analyse stratégique" },
    { v: "structuration", l: "Structuration" },
    { v: "qualification", l: "Qualification" },
    { v: "discussion", l: "Discussions" },
    { v: "closing", l: "Closing" },
    { v: "live", l: "En cours" },
    { v: "paused", l: "Pause" },
    { v: "closed", l: "Clos" },
];

const CONFIDENTIALITY = [
    { v: "standard", l: "Standard" },
    { v: "restricted", l: "Restreint" },
    { v: "ultra", l: "Ultra-confidentiel" },
];

function statusLabel(v) {
    return STATUS.find((s) => s.v === v)?.l || v;
}

export default function CockpitDossiers() {
    const [items, setItems] = useState([]);
    const [creating, setCreating] = useState(false);

    const load = () => cockpit.get("/dossiers").then(({ data }) => setItems(data));

    useEffect(() => {
        load();
    }, []);

    return (
        <div data-testid="cockpit-dossiers" className="px-5 sm:px-8 lg:px-16 py-10 lg:py-14">
            <div className="flex items-end justify-between mb-12 gap-6">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-4">
                        Pipeline confidentiel
                    </div>
                    <h1
                        className="font-serif text-[28px] sm:text-[34px] md:text-[40px] lg:text-[48px] leading-[1.08] text-[#f4ecd8]"
                        style={{ fontWeight: 300 }}
                    >
                        Dossiers.
                    </h1>
                </div>
                <button
                    type="button"
                    data-testid="cockpit-new-dossier"
                    onClick={() => setCreating(true)}
                    className="group inline-flex items-center gap-3 px-6 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                >
                    <Plus strokeWidth={1.2} size={14} />
                    Nouveau dossier
                </button>
            </div>

            <div className="border-t border-white/[0.06]">
                {items.length === 0 ? (
                    <div className="py-20 text-center">
                        <FolderLock
                            strokeWidth={0.8}
                            size={42}
                            className="text-[#d4af37]/40 mx-auto mb-6"
                        />
                        <p className="text-[#f4ecd8]/50 text-[14px]">
                            Aucun dossier dans le cockpit.{" "}
                            <button
                                type="button"
                                onClick={() => setCreating(true)}
                                className="text-[#d4af37] underline-offset-4 hover:underline"
                            >
                                Initier le premier dossier
                            </button>
                            .
                        </p>
                    </div>
                ) : (
                    items.map((d) => (
                        <Link
                            key={d.id}
                            to={`/cockpit/dossiers/${d.id}`}
                            data-testid={`dossier-row-${d.id}`}
                            className="group grid grid-cols-[1fr_auto_auto_auto] items-center gap-6 py-6 border-b border-white/[0.06] hover:bg-white/[0.02] transition-colors duration-500 px-2"
                        >
                            <div className="min-w-0">
                                <div
                                    className="font-serif text-[20px] md:text-[22px] text-[#f4ecd8] leading-tight truncate"
                                    style={{ fontWeight: 400 }}
                                >
                                    {d.name}
                                </div>
                                <div className="mt-1.5 text-[12px] text-[#f4ecd8]/45 tracking-wide">
                                    {d.sector || "Secteur n.c."}
                                    {d.geography ? ` · ${d.geography}` : ""}
                                </div>
                            </div>
                            <div className="text-[11px] text-[#f4ecd8]/60 tracking-wide hidden md:block">
                                {d.ticket || "—"}
                            </div>
                            <div className="text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/80">
                                {statusLabel(d.status)}
                            </div>
                            <ArrowUpRight
                                strokeWidth={1.2}
                                size={14}
                                className="text-[#f4ecd8]/30 group-hover:text-[#d4af37] group-hover:translate-x-0.5 transition-all duration-500"
                            />
                        </Link>
                    ))
                )}
            </div>

            {creating && (
                <NewDossierModal
                    onClose={() => setCreating(false)}
                    onCreated={() => {
                        setCreating(false);
                        load();
                    }}
                />
            )}
        </div>
    );
}

function NewDossierModal({ onClose, onCreated }) {
    const nav = useNavigate();
    const [form, setForm] = useState({
        name: "",
        sector: "",
        geography: "",
        operation_type: "",
        maturity: "",
        ticket: "",
        confidentiality: "restricted",
        status: "audit",
        summary: "",
        pipeline_stage: "qualification",
        source: "",
        maturity_level: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const onChange = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const { data } = await cockpit.post("/dossiers", form);
            onCreated();
            nav(`/cockpit/dossiers/${data.id}`);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Impossible de créer le dossier");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="new-dossier-modal"
            className="fixed inset-0 z-50 bg-[#070d18]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
            onClick={onClose}
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0b1220] border border-white/10 w-full max-w-[640px] p-10 lg:p-14 mt-10 mb-20 relative"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-6 right-6 text-[#f4ecd8]/50 hover:text-[#f4ecd8] transition-colors duration-500"
                    aria-label="Fermer"
                >
                    <X strokeWidth={1.2} size={20} />
                </button>
                <div className="overline text-[#d4af37]/80 mb-5">Nouveau mandat</div>
                <h2
                    className="font-serif text-[28px] text-[#f4ecd8] mb-10"
                    style={{ fontWeight: 300 }}
                >
                    Cadrer un dossier confidentiel.
                </h2>

                <div className="space-y-7">
                    <Field
                        label="Nom du projet"
                        required
                        value={form.name}
                        onChange={onChange("name")}
                        testid="new-dossier-name"
                    />
                    <div className="grid sm:grid-cols-2 gap-7">
                        <Field
                            label="Secteur"
                            value={form.sector}
                            onChange={onChange("sector")}
                        />
                        <Field
                            label="Géographie"
                            value={form.geography}
                            onChange={onChange("geography")}
                        />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-7">
                        <Field
                            label="Type d'opération"
                            value={form.operation_type}
                            onChange={onChange("operation_type")}
                        />
                        <Field
                            label="Ticket recherché"
                            value={form.ticket}
                            onChange={onChange("ticket")}
                        />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-7">
                        <Field
                            label="Maturité"
                            value={form.maturity}
                            onChange={onChange("maturity")}
                        />
                        <Select
                            label="Confidentialité"
                            value={form.confidentiality}
                            onChange={onChange("confidentiality")}
                            options={CONFIDENTIALITY}
                        />
                    </div>
                    <Select
                        label="Statut initial"
                        value={form.status}
                        onChange={onChange("status")}
                        options={STATUS}
                    />
                    <div className="pt-4 border-t border-white/[0.06]">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/70 mb-5">
                            Pilotage commercial
                        </div>
                        <div className="grid sm:grid-cols-3 gap-7">
                            <Select
                                label="Phase pipeline"
                                value={form.pipeline_stage}
                                onChange={onChange("pipeline_stage")}
                                options={PIPELINE_STAGES}
                                testid="new-dossier-pipeline-stage"
                            />
                            <Select
                                label="Origine"
                                value={form.source}
                                onChange={onChange("source")}
                                options={[{ v: "", l: "—" }, ...SOURCE_OPTIONS_LIST]}
                                testid="new-dossier-source"
                            />
                            <Select
                                label="Maturité"
                                value={form.maturity_level}
                                onChange={onChange("maturity_level")}
                                options={[{ v: "", l: "—" }, ...MATURITY_LEVELS_LIST]}
                                testid="new-dossier-maturity-level"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                            Synthèse stratégique
                        </label>
                        <textarea
                            rows={4}
                            value={form.summary}
                            onChange={onChange("summary")}
                            className="w-full bg-transparent border border-white/15 focus:border-[#d4af37] outline-none p-3 text-[14px] font-serif text-[#f4ecd8] resize-none"
                        />
                    </div>
                </div>

                {err && (
                    <div className="mt-6 text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1">
                        {err}
                    </div>
                )}

                <div className="mt-10 flex justify-end gap-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-3 text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/60 hover:text-[#f4ecd8] transition-colors duration-500"
                    >
                        Annuler
                    </button>
                    <button
                        type="submit"
                        data-testid="new-dossier-submit"
                        disabled={busy}
                        className="px-7 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {busy ? "Création…" : "Créer le dossier"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export function Field({ label, value, onChange, required, testid, type = "text" }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                {label}
                {required ? " *" : ""}
            </label>
            <input
                data-testid={testid}
                type={type}
                required={required}
                value={value}
                onChange={onChange}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2.5 text-[15px] font-serif text-[#f4ecd8] transition-colors duration-500"
            />
        </div>
    );
}

export function Select({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#d4af37]/80 mb-3">
                {label}
            </label>
            <select
                value={value}
                onChange={onChange}
                data-testid={testid}
                className="w-full bg-[#0b1220] border-b border-white/15 focus:border-[#d4af37] outline-none py-2.5 text-[15px] font-serif text-[#f4ecd8] transition-colors duration-500"
            >
                {options.map((o) => (
                    <option key={o.v} value={o.v} className="bg-[#0b1220]">
                        {o.l}
                    </option>
                ))}
            </select>
        </div>
    );
}
